/**
 * CENTER-PARTING SLIDERS (2026-09-30, Matt: "I have the multi slide set to split both
 * ways (center parting) but it is modeled as though all panels are passing each other.
 * It should be two panels thick (the outer panels are fixed), not four panels thick").
 *
 * The TRACK each panel of an N-panel multi-slide rides on — the one rule the plan
 * symbol and the 3D prototype share. One-way (L / R): every panel its own track, the
 * train stepping across the wall (unchanged). SPLIT: the two halves MIRROR — the panel
 * at each jamb (the outer, fixed one) sits on track 0, the next pair in on track 1, …,
 * so a 4-panel center-parting door is 2 tracks deep, a 6-panel is 3. Returns the per-
 * panel track index and the track count.
 */
export function slideLanes(n: number, direction: string): { lanes: number[]; count: number } {
  const N = Math.max(1, Math.round(n) || 1)
  if (direction === 'split' && N >= 2) {
    const lanes = Array.from({ length: N }, (_, k) => Math.min(k, N - 1 - k))
    return { lanes, count: Math.max(...lanes) + 1 }
  }
  return { lanes: Array.from({ length: N }, (_, k) => k), count: N }
}

/**
 * MEETING PANELS TOUCH (2026-09-30, Matt: "The multi-slide glass doors when set to center
 * parting have overlapping center panels at the middle. They should touch, not overlap").
 * Neighbouring panels overlap at their meeting stiles only when they ride DIFFERENT tracks
 * (one slides past the other and the stiles seal); two panels on the SAME track — a
 * center-parting door's middle pair — can't pass each other, so they butt edge to edge.
 * True when panel i and panel i+1 overlap. Shared by the plan symbol, the 3D panels and
 * the elevation figure, so the three can never disagree.
 */
export function slideNeighboursOverlap(lanes: number[], i: number): boolean {
  return i >= 0 && i + 1 < lanes.length && lanes[i] !== lanes[i + 1]
}

/**
 * BI-PARTING SLIDERS FACE THE ROOM (2026-09-30, Matt: "Sliding bi-parting doors should
 * know which side of the wall is exterior and make sure the bi-parting doors are set to
 * the interior side (the operating door panels are inside toward the interior of the
 * room)"). Which way the HIGHER tracks (the operating, inner panels of a split door) step
 * across the wall, along the host wall's left normal: toward its INTERIOR face. A wall's
 * exterior face is its left side unless `flipped` (wallFaceOffsets), so the interior is
 * −n unflipped, +n flipped.
 *
 * ONE-WAY TRAINS TOO (2026-10-07, Matt: multi-slides "don't appear to orient correctly to
 * the exterior side of the wall. Right now they look to stack in the opposite direction
 * they should … on a single directional door"): a one-way train used to keep +n stepping,
 * which on an unflipped wall put the park-side panel (track 0 — the fixed, weather-sealing
 * panel of a stacking door) on the INTERIOR and the operating panels outboard of it. Real
 * stacking and pocketing multi-slides do the opposite: the outermost track is the fixed
 * panel at the exterior, each operating panel steps one track further into the room. So
 * the rule is the same for every direction now.
 */
export function slideInteriorSign(_direction: string, hostFlipped: boolean | undefined): 1 | -1 {
  return hostFlipped === true ? 1 : -1
}
